// Hub ▸ Marketing — where the Marketing Manager / CMO runs every marketing
// dollar and the CEO reads the results (plan: docs/marketing/marketing-hub-plan.md).
//
// Tabs: Overview · Journeys · Customers · Explore · Ask · Spend & Budgets ·
// Channels & Campaigns · Email · Social (S2 + S3, CEO 2026-10-21; Explore +
// Ask, S4, CEO 2026-10-22).
// Full width, large type, brand tokens. Finance reads; admin + marketing edit.
import { NavLink, Navigate, Route, Routes, useLocation, useMatch } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { canEditMarketing, isMarketingOnly } from "@/lib/access";
import { cn } from "@/lib/utils";
import { OverviewTab } from "./OverviewTab";
import { JourneysTab } from "./JourneysTab";
import { CustomersTab } from "./CustomersTab";
import { ExploreTab } from "./ExploreTab";
import { AskTab } from "./AskTab";
import { BudgetsTab } from "./BudgetsTab";
import { ChannelsTab } from "./ChannelsTab";
import { SocialTab } from "./SocialTab";
import { EmailTab } from "./EmailTab";

const tabs = [
  { to: "overview", label: "Overview" },
  { to: "journeys", label: "Journeys" },
  { to: "customers", label: "Customers" },
  { to: "explore", label: "Explore" },
  { to: "ask", label: "Ask" },
  { to: "spend", label: "Spend & Budgets" },
  { to: "channels", label: "Channels & Campaigns" },
  { to: "email", label: "Email" },
  { to: "social", label: "Social" },
];

export function MarketingPage() {
  const base = useMatch("/marketing/*")?.pathnameBase ?? "/marketing";
  const { search } = useLocation();
  const { user } = useAuth();
  // Finance reads Marketing; budgets, drafting and uploads are marketing + admin (CEO 2026-10-21).
  const readOnly = !canEditMarketing(user?.roles);
  // Keep the chosen dates when moving between Overview and Channels.
  const keep = new URLSearchParams(search);
  const carry = ["period", "from", "to"].filter((k) => keep.get(k)).map((k) => `${k}=${encodeURIComponent(keep.get(k)!)}`).join("&");

  return (
    <div className="w-full px-4 py-6 md:px-8 md:py-8">
      <header className="flex flex-col gap-1">
        <h1 className="text-title font-semibold tracking-tight text-ink" style={{ fontSize: "clamp(32px, 4vw, 40px)" }}>Marketing</h1>
        <p className="text-body text-ink-muted">What every marketing dollar bought: leads, clients{isMarketingOnly(user?.roles) ? " and revenue" : ", revenue and gross profit"} — by channel, campaign and month.</p>
      </header>
      {readOnly && (
        <p className="mt-4 rounded-md border border-rule bg-paper px-4 py-2.5 text-label text-ink-muted">
          You're viewing Marketing read-only. Budgets and drafting are for the marketing team and admins.
        </p>
      )}

      {/* Tabs scroll sideways on phones; the fade on the right says there's more. */}
      <div className="relative mt-6">
      <nav aria-label="Marketing" className="relative overflow-x-auto border-b border-rule">
        <div className="flex min-w-max gap-1 pr-8 sm:pr-0">
          {tabs.map((tab) => (
            <NavLink key={tab.to} to={`${base}/${tab.to}${carry && (tab.to === "overview" || tab.to === "channels") ? `?${carry}` : ""}`}
              className={({ isActive }) => cn("-mb-px border-b-2 px-4 py-3 text-label transition-colors",
                isActive ? "border-ink font-semibold text-ink" : "border-transparent text-ink-muted hover:text-ink")}>
              {tab.label}
            </NavLink>
          ))}
        </div>
      </nav>
      <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#F5F5F2] to-transparent sm:hidden" />
      </div>

      <div className="mt-6">
        <Routes>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<OverviewTab />} />
          <Route path="journeys" element={<JourneysTab />} />
          <Route path="customers" element={<CustomersTab />} />
          <Route path="explore" element={<ExploreTab />} />
          <Route path="ask" element={<AskTab />} />
          <Route path="spend" element={<BudgetsTab />} />
          <Route path="channels" element={<ChannelsTab />} />
          <Route path="email" element={<EmailTab />} />
          <Route path="social" element={<SocialTab />} />
          <Route path="*" element={<Navigate to="overview" replace />} />
        </Routes>
      </div>
    </div>
  );
}
